// The one way into the text (`EditingRules.decide` and `ScriveningsTextView.shouldChangeText`): every transaction
// that changes the document passes this filter, which accepts it, declines it, or replaces it with what the rules make
// of it — balanced for hidden syntax, split at fragment boundaries (each piece balanced), newlines normalised, typed
// text styled after ⌘I/⌘B. Selection-only transactions get the caret rules (`ScriveningsSession.caret`).
import {
  EditorSelection, EditorState, findClusterBreak, Transaction, type ChangeSpec, type Extension, type TransactionSpec,
} from '@codemirror/state';
import type { Layout, BodyEdit } from './layout.ts';
import { hiddenTester, isHiddenAt } from './markdown.ts';
import { balancingHiddenSyntax, emphasised, inserting, normalizeNewlines, typedPieces, type Fix } from './rules.ts';
import { windowAround } from './session.ts';
import { declinesSubstitution, pastedScript, transformTyped } from './screenplay.ts';
import { captured, deleteRange, inputDeletion, removeListMarker } from './commands.ts';
import {
  Exact, ExactCaret, External, isHistory, isLocked, layoutOf, pendingEmphasisField, showsMarks, type PendingEmphasis,
} from './state.ts';

/** What to do with one change (`EditingRules.EditDecision`), in document offsets. */
export type Decision =
  | { type: 'accept' }
  | { type: 'decline' }
  | { type: 'refuse' }
  /** `asGiven`: the same edit with its newlines normalised, so the selection is the input's own (as on the Mac, where
   *  the text view re-inserts it and places the caret itself): the fix's caret only when the input set one. */
  | { type: 'replace'; fix: Fix; asGiven?: boolean }
  | { type: 'split'; edits: BodyEdit[]; caret: number };

export interface DecideOptions {
  /** False for the editor's own exact edits: made as given. */
  balance: boolean;
  /** The caret when nothing is selected (Backspace/Delete on hidden syntax beside it). */
  caret: number | null;
  pending?: PendingEmphasis | null;
}

/** `EditingRules.decide`: what replacing [a, b) with `text` should do. */
export function decide(state: EditorState, a: number, b: number, text: string, o: DecideOptions): Decision {
  const layout = layoutOf(state);
  if (layout.count === 0) return { type: 'refuse' };
  // A locked document: nothing goes in.
  if (isLocked(state, a, b)) return { type: 'refuse' };
  const at = layout.bodyAt(a);
  const screenplay = layout.isScreenplay(at);
  // Typing after ⌘I or ⌘B with nothing selected: the typed text takes the style.
  const pending = o.pending;
  if (pending && o.balance && !screenplay && a === b && a === pending.pos && text && !text.includes('\n') && at >= 0) {
    const { w, base } = windowAround(state, at, a, b);
    return { type: 'replace', fix: shift(emphasised(w, text, a - base, pending.marker), base) };
  }
  if (screenplay && declinesSubstitution(state, a, b, text)) return { type: 'decline' };
  if (o.balance) {
    // Newlines from any source (keyboard, dictation, paste, drop) become paragraph breaks. In a screenplay a single
    // newline means something: dialogue follows its cue.
    const normalized = screenplay ? pastedScript(text) : normalizeNewlines(text);
    if (normalized !== text) {
      const d = decide(state, a, b, normalized, { ...o, pending: null });
      return d.type === 'accept' ? { type: 'replace', fix: { from: a, to: b, text: normalized, caret: a + normalized.length }, asGiven: true } : d;
    }
    // A line break inside emphasis: closed before it and opened again after, as the dialect reads each line alone.
    const i = layout.within(a, b);
    if (!screenplay && text.includes('\n') && i >= 0) {
      const { w, base } = windowAround(state, i, a, b);
      const fix = inserting(w, typedPieces(w, text, a - base, b - base), a - base, b - base);
      if (fix.from + base !== a || fix.to + base !== b || fix.text !== text) return { type: 'replace', fix: shift(fix, base) };
    }
  }
  const edits = layout.plan(a, b, text);
  if (edits.length === 0) return { type: 'refuse' };
  if (edits.length === 1 && edits[0].from === a && edits[0].to === b) {
    if (o.balance && !screenplay && !showsMarks(state)) {
      const { w, base } = windowAround(state, edits[0].index, a, b);
      const fix = balancingHiddenSyntax(w, a - base, b - base, text, o.caret === null ? null : o.caret - base);
      if (fix) return { type: 'replace', fix: shift(fix, base) };
    }
    return { type: 'accept' };
  }
  return o.balance ? balancedSplit(state, edits) : { type: 'split', edits, caret: edits[0].from + edits[0].text.length };
}

const shift = (f: Fix, base: number): Fix => ({ from: f.from + base, to: f.to + base, text: f.text, caret: f.caret + base });

/** An edit across a fragment boundary, each fragment's piece balanced as an edit inside that fragment would be
 *  (`EditingRules.balancedSplit`); screenplay pieces are made as given, pieces that balance to no change dropped. */
function balancedSplit(state: EditorState, edits: BodyEdit[]): Decision {
  const layout = layoutOf(state);
  let caret = edits[0].from + edits[0].text.length;
  const out: BodyEdit[] = [];
  edits.forEach((e, n) => {
    if (layout.isScreenplay(e.index) || showsMarks(state)) { out.push(e); return; }
    const { w, base } = windowAround(state, e.index, e.from, e.to);
    const f = balancingHiddenSyntax(w, e.from - base, e.to - base, e.text, null);
    if (!f) { out.push(e); return; }
    const fix = shift(f, base);
    if (n === 0) caret = fix.caret;
    if (state.doc.sliceString(fix.from, fix.to) === fix.text) return;
    out.push({ index: e.index, from: fix.from, to: fix.to, text: fix.text });
  });
  return { type: 'split', edits: out, caret };
}

/** A transaction spec making a decision's edit, marked exact so it isn't decided again. */
export function specFor(d: Decision, userEvent: string): TransactionSpec | null {
  const annotations = [Exact.of(true), ExactCaret.of(true), Transaction.userEvent.of(userEvent)];
  if (d.type === 'replace') {
    const f = d.fix;
    if (f.from === f.to && f.text === '') return null;
    return { changes: { from: f.from, to: f.to, insert: f.text }, selection: EditorSelection.cursor(f.caret), annotations, scrollIntoView: true };
  }
  if (d.type === 'split') {
    if (d.edits.length === 0) return { selection: EditorSelection.cursor(d.caret), annotations: [ExactCaret.of(true)] };
    const changes: ChangeSpec[] = d.edits.map((e) => ({ from: e.from, to: e.to, insert: e.text }));
    return { changes, selection: EditorSelection.cursor(d.caret), annotations, scrollIntoView: true };
  }
  return null;
}

interface Change { a: number; b: number; text: string }
function changesOf(tr: Transaction): Change[] {
  const out: Change[] = [];
  tr.changes.iterChanges((a, b, _fb, _tb, ins) => out.push({ a, b, text: ins.toString() }));
  return out;
}

/** Counts, for tests and the bench. */
export const metrics = { accepted: 0, balanced: 0, split: 0, refused: 0 };

function filterEdit(tr: Transaction): Transaction | TransactionSpec | readonly TransactionSpec[] {
  const state = tr.startState;
  const layout = layoutOf(state);
  const exact = tr.annotation(Exact) === true || isHistory(tr);
  const userEvent = tr.annotation(Transaction.userEvent) ?? 'input';
  const changes = changesOf(tr);
  const sel = state.selection.main;
  const pending = state.field(pendingEmphasisField);

  if (changes.length !== 1) {
    // Several changes at once (the editor's own multi-part commands, undo, find-and-replace, multiple cursors): let
    // through when each is a plain edit inside one body; otherwise each is decided and made alone (one undo step),
    // and any that would cross a boundary is left out (`shouldChangeText(inRanges:)`).
    if (exact) {
      if (changes.every((c) => layout.within(c.a, c.b) >= 0)) { metrics.accepted++; return tr; }
      metrics.refused++;
      return {};
    }
    const decisions = changes.map((c) => ({ c, d: decide(state, c.a, c.b, c.text, { balance: true, caret: null }) }));
    if (decisions.every(({ d }) => d.type === 'accept')) { metrics.accepted++; return tr; }
    const specs: ChangeSpec[] = [];
    let last = -1;
    for (const { c, d } of decisions) {
      const edit = d.type === 'accept' ? { from: c.a, to: c.b, insert: c.text }
        : d.type === 'replace' ? { from: d.fix.from, to: d.fix.to, insert: d.fix.text } : null;
      if (!edit || edit.from < last) continue;
      specs.push(edit);
      last = edit.to;
    }
    return { changes: specs, annotations: [Exact.of(true), Transaction.userEvent.of(userEvent)] };
  }

  const c = changes[0];
  // An input method's composition is the browser's: rewriting it breaks the composition. It's kept to one body.
  if (tr.isUserEvent('input.type.compose')) {
    if (layout.within(c.a, c.b) >= 0) { metrics.accepted++; return tr; }
    metrics.refused++;
    return {};
  }
  let { a, b, text } = c;
  let transformed = false;
  // A deletion beside the caret from the input system (Android's keyboards send no Backspace key; execCommand) is
  // made as the key would make it.
  if (!exact && text === '' && tr.isUserEvent('input')) {
    const direction = inputDeletion(state, a, b);
    if (direction) {
      if (direction === 'backward') { const lm = captured(removeListMarker, state); if (lm) return lm; }
      const range = deleteRange(state, direction === 'backward');
      if (!range) return {};
      [a, b] = range;
      transformed = true;
    }
  }
  // Typing in a screenplay's headings, cues and transitions comes out in capitals (`ScreenplayKeys.transform`).
  if (!exact && tr.isUserEvent('input.type')) {
    const t = transformTyped(state, a, b, text);
    if (t) { ({ a, b, text } = t); transformed = true; }
  }
  const d = decide(state, a, b, text, { balance: !exact, caret: sel.empty ? sel.head : null, pending });
  switch (d.type) {
    case 'accept':
      metrics.accepted++;
      if (!transformed) return tr;
      return {
        changes: { from: a, to: b, insert: text }, selection: EditorSelection.cursor(a + text.length),
        annotations: [Exact.of(true), Transaction.userEvent.of(userEvent)], scrollIntoView: true,
      };
    case 'decline':
    case 'refuse':
      metrics.refused++;
      return {};
    case 'replace':
      metrics.balanced++;
      // Normalised text replacing a range the input didn't put the caret after (an insertion away from the selection):
      // the selection stays where it was, moved only by the edit, as NSTextView leaves it.
      if (d.asGiven && !tr.selection) {
        return { changes: { from: d.fix.from, to: d.fix.to, insert: d.fix.text }, annotations: [Exact.of(true), Transaction.userEvent.of(userEvent)], scrollIntoView: true };
      }
      return specFor(d, userEvent) ?? {};
    case 'split':
      metrics.split++;
      return specFor(d, userEvent) ?? {};
  }
}

// MARK: caret rules (`ScriveningsSession.caret`)

/** Whether the character at `i` is hidden syntax, reading each line once (per call site). */
export function hiddenAt(state: EditorState, o: { evenWhenShown?: boolean } = {}): (i: number) => boolean {
  if (showsMarks(state) && !o.evenWhenShown) return () => false;
  const layout = layoutOf(state);
  const lines = new Map<number, { from: number; test: (k: number) => boolean }>();
  return (i: number) => {
    if (i < 0 || i >= state.doc.length) return false;
    const b = layout.bodyAt(i);
    if (b < 0 || i >= layout.to[b] || layout.isScreenplay(b)) return false;
    const line = state.doc.lineAt(i);
    let l = lines.get(line.from);
    if (!l) {
      // A list marker's line is read with its block; a window holding it is enough.
      const { w, base } = windowAround(state, b, line.from, line.to);
      const test = hiddenTester(w.t);
      l = { from: base, test };
      lines.set(line.from, l);
    }
    return l.test(i - l.from);
  };
}

/** Where a caret moving from `old` to `pos` should rest (`ScriveningsSession.caret`): a move that crossed only hidden
 *  syntax goes on across one visible character (never across a line break), so the arrows never seem to stick; a
 *  caret landing inside a run of hidden syntax goes to the run's end. `leftStep`: the left arrow, which CodeMirror moves
 *  across a whole atomic run: across a long one (a link's hidden address) it goes on too, as NSTextView's does (the
 *  right arrow stops at the run's end, outside the link, as there). */
export function caretRule(state: EditorState, old: number, pos: number, leftStep = false): number {
  if (showsMarks(state)) return pos;
  const hidden = hiddenAt(state);
  const doc = state.doc;
  const layout = layoutOf(state);
  const lo = Math.min(old, pos), hi = Math.max(old, pos);
  if (pos !== old && (hi - lo <= 8 || (leftStep && pos < old))) {
    let all = true;
    for (let k = lo; k < hi; k++) if (!hidden(k)) { all = false; break; }
    if (all) {
      let i = pos;
      if (pos > old) {
        while (i < doc.length && hidden(i)) i++;
        if (i < doc.length && doc.sliceString(i, i + 1) !== '\n') {
          const s = doc.sliceString(i, Math.min(doc.length, i + 16));
          i += findClusterBreak(s, 0, true);
        }
      } else {
        while (i > 0 && hidden(i - 1)) i--;
        if (i > 0 && doc.sliceString(i - 1, i) !== '\n') {
          const s = doc.sliceString(Math.max(0, i - 16), i);
          i = i - s.length + findClusterBreak(s, s.length, false);
        }
      }
      return layout.snap(i);
    }
  }
  if (hidden(pos - 1) && hidden(pos)) {
    let i = pos;
    while (hidden(i)) i++;
    return i;
  }
  return pos;
}

/** Selection changes (`setSelectedRanges`): a caret never rests in a divider, and a move of the caret alone (arrows,
 *  clicks, the app placing it) follows the caret rules. A selection is left as it is. */
function filterSelection(tr: Transaction): Transaction | TransactionSpec {
  if (!tr.selection) return tr;
  const state = tr.startState;
  const layout = layoutOf(state);
  if (layout.count === 0) return tr;
  const sel = tr.selection;
  const navigating = tr.annotation(ExactCaret) !== true && sel.ranges.length === 1;
  // An arrow key (CodeMirror's cursor motion), not a click or the app placing the caret.
  const step = tr.annotation(Transaction.userEvent) === 'select';
  const old = state.selection.main;
  let changed = false;
  const ranges = sel.ranges.map((range) => {
    if (!range.empty) return range;
    let head = layout.snap(range.head);
    if (navigating) head = old.empty ? caretRule(state, old.head, head, step) : caretRule(state, head, head);
    if (head === range.head) return range;
    changed = true;
    return EditorSelection.cursor(head, range.assoc);
  });
  if (!changed) return tr;
  const event = tr.annotation(Transaction.userEvent);
  return {
    selection: EditorSelection.create(ranges, sel.mainIndex),
    scrollIntoView: tr.scrollIntoView,
    effects: tr.effects,
    annotations: event ? [Transaction.userEvent.of(event)] : [],
  };
}

/** The filter itself. External changes (loading, sync) pass untouched. */
export const editingFilter: Extension = EditorState.transactionFilter.of((tr) => {
  if (tr.annotation(External)) return tr;
  if (!tr.docChanged) return filterSelection(tr);
  return filterEdit(tr);
});

export { isHiddenAt };
export type { Layout };
